/**
 * useDataGridVirtualization
 *
 * Wraps @tanstack/react-virtual for optional row virtualization.
 * Enabled when rowCount > VIRTUALIZATION_THRESHOLD.
 * Always calls useVirtualizer (React hooks rules), but sets count=0 when disabled.
 */

import { useVirtualizer, type Virtualizer } from '@tanstack/react-virtual';
import type { TableDensity } from '@/lib/store/preferencesStore';

const VIRTUALIZATION_THRESHOLD = 50;
const OVERSCAN = 5;

export const DENSITY_ROW_HEIGHT: Record<TableDensity, number> = {
  compact: 36,
  default: 44,
  comfortable: 56,
  spacious: 72,
};

export interface UseDataGridVirtualizationOptions {
  rowCount: number;
  parentRef: React.RefObject<HTMLDivElement | null>;
  enabled: boolean;
  density?: TableDensity;
}

export interface UseDataGridVirtualizationReturn {
  virtualizer: Virtualizer<HTMLDivElement, Element>;
  isVirtualized: boolean;
  totalSize: number;
}

export function useDataGridVirtualization(
  opts: UseDataGridVirtualizationOptions,
): UseDataGridVirtualizationReturn {
  const shouldVirtualize = opts.enabled && opts.rowCount > VIRTUALIZATION_THRESHOLD;

  // Always call useVirtualizer — React hooks must be unconditional.
  // When disabled, count=0 makes it a no-op.
  const virtualizer = useVirtualizer({
    count: shouldVirtualize ? opts.rowCount : 0,
    getScrollElement: () => opts.parentRef.current,
    estimateSize: () => DENSITY_ROW_HEIGHT[opts.density ?? 'comfortable'],
    overscan: OVERSCAN,
    measureElement: (el) => el.getBoundingClientRect().height,
  });

  return {
    virtualizer,
    isVirtualized: shouldVirtualize,
    totalSize: shouldVirtualize ? virtualizer.getTotalSize() : 0,
  };
}
